import { useState, type ReactNode } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { StatusDot, type DotVariant } from '@/design-system/components/primitives/status-dot'
import { worstVariant } from '@/design-system/tokens/status'

/**
 * TrafficRow — строка журнала со светофором по всем направлениям (`.tr-row`/`.cell`/`.chip`/
 * `.c-prochee`/`.detail`/`.dir-row`/`.idx` макета).
 *
 * Инвариант: цвет строки = ХУДШИЙ статус по ВСЕМ направлениям сотрудника, включая скрытые за
 * «+N»/«Прочее», по приоритету red > amber > green > gray. Лишние направления сворачиваются в «+N»;
 * в раскрытом списке у каждого направления — под-метка idx (А/Б/СИЗ/ПП, «III группа»). Статусные
 * цвета — из светофор-токенов.
 *
 * MR !17 (centrika-docs, `page.occupational-safety.training`): в строке два отдельных интерактивных
 * элемента — ссылка с ФИО (`employeeHref`, необязательна) и кнопка-шеврон раскрытия с
 * `aria-expanded`. Клик по остальной площади строки переключает раскрытие; переход по ссылке
 * раскрытие не меняет. С клавиатуры доступны ссылка и кнопка (раньше вся строка была одной
 * кнопкой-триггером).
 *
 * F2-16: чипы направлений и строка заголовков колонок (`TrafficRowHeader`) выровнены по общей
 * сетке — `trafficGridTemplate` строит ОДИНАКОВЫЙ `grid-template-columns` для заголовка и для
 * каждого TrafficRow, поэтому N-й чип всегда стоит под N-м заголовком независимо от содержимого.
 * Колонка «Прочее»: проп `showOtherColumn` резервирует место под агрегат скрытых направлений даже
 * у строк без своего «Прочее» (тогда клетка — нейтральный «—»), чтобы колонки не съезжали.
 *
 * Свободная ширина делится между колонкой сотрудника и колонками направлений (замечание
 * заказчика): раньше её целиком забирала колонка сотрудника — между ФИО и чипами оставалась
 * пустота, а узкие колонки направлений переносили «Пожарная безопасность» посреди слова. Чип
 * стоит по центру своей колонки, под центрированным заголовком, и не растягивается на всю её
 * ширину (не уже прежних 6rem).
 */

/** Ширины колонок направлений — общие для TrafficRowHeader и чипов TrafficRow (выравнивание). */
const TRAFFIC_NAME_COL = 'minmax(14rem,3fr)'
/** Колонка направления (и «Прочее»): минимум вмещает заголовок из слов вроде «безопасность». */
const TRAFFIC_CHIP_COL = 'minmax(6.5rem,1fr)'
const TRAFFIC_CHEVRON_COL = '1rem'

/** Чип клетки: по центру колонки, не уже 6rem, а не растянут на всю её ширину. */
const CHIP_CLASS =
  'min-w-24 justify-self-center rounded-8 px-2 py-1.5 text-center text-12 font-medium'

function trafficGridTemplate(chipCount: number, showOther: boolean): string {
  const cols = [TRAFFIC_NAME_COL, ...Array.from({ length: chipCount }, () => TRAFFIC_CHIP_COL)]
  if (showOther) cols.push(TRAFFIC_CHIP_COL)
  cols.push(TRAFFIC_CHEVRON_COL)
  return cols.join(' ')
}

export interface TrafficDirection {
  name: string
  /** Короткий код направления/группы (А/Б/СИЗ/ПП, «III группа»). */
  idx?: string
  status: DotVariant
  /** Короткий срок (например, дата окончания). */
  term?: string
}

export interface TrafficRowProps {
  employee: string
  /**
   * Ссылка на страницу сотрудника (centrika-docs, `page.occupational-safety.training`, MR !17):
   * при её наличии ФИО рендерится ссылкой, отдельной от кнопки раскрытия. Компонент остаётся
   * независимым от роутера — SPA-переход делает родитель в `onEmployeeClick`.
   */
  employeeHref?: string
  /** Клик по ссылке ФИО (родитель гасит событие и уводит через роутер). */
  onEmployeeClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  /** Подпись под именем (должность/подразделение). */
  sub?: string
  directions: TrafficDirection[]
  /** Сколько направлений показывать чипами до свёртки «+N». */
  visibleCount?: number
  /**
   * Явный цвет строки (худший по ВСЕМ направлениям, включая скрытые). По умолчанию вычисляется из
   * `directions`. Журнал сроков считает худший на сервере по всем направлениям и передаёт его сюда.
   */
  rowStatus?: DotVariant
  /**
   * Агрегат скрытых «ролевых/прочих» направлений: цветной чип «+N» (цвет — худший из скрытых).
   * Приоритетнее нативной свёртки `directions.slice(visibleCount)`.
   */
  extra?: { count: number; status: DotVariant }
  /** Направления в раскрытии (по умолчанию = `directions`). Журнал раскрывает полный список записей. */
  details?: TrafficDirection[]
  /** Доп. контент в конце раскрытия (например, кнопка «Напомнить»). */
  footer?: ReactNode
  /**
   * Резервировать колонку «Прочее» под общим заголовком (F2-16), даже если у ЭТОЙ строки своего
   * «Прочее» нет — тогда клетка рисуется нейтральным «—». Задаётся ОДИНАКОВО для всех строк
   * таблицы (истинно, если хоть у кого-то есть скрытые направления), иначе колонки разъедутся
   * относительно `TrafficRowHeader`. Своя строка со скрытыми направлениями резервирует колонку
   * независимо от этого пропа.
   */
  showOtherColumn?: boolean
  className?: string
}

const STATUS_WORD: Record<DotVariant, string> = {
  red: 'просрочено',
  amber: 'истекает скоро',
  green: 'в порядке',
  gray: 'нет данных',
}

const CHIP_BY_STATUS: Record<DotVariant, string> = {
  red: 'bg-red-bg text-red',
  amber: 'bg-amber-bg text-amber',
  green: 'bg-green-bg text-green',
  gray: 'bg-gray-bg text-gray',
}

const BORDER_BY_WORST: Record<DotVariant, string> = {
  red: 'border-l-red',
  amber: 'border-l-amber',
  green: 'border-l-green',
  gray: 'border-l-line-strong',
}

export function TrafficRow({
  employee,
  employeeHref,
  onEmployeeClick,
  sub,
  directions,
  visibleCount = 3,
  rowStatus,
  extra,
  details,
  footer,
  showOtherColumn,
  className,
}: TrafficRowProps) {
  const [open, setOpen] = useState(false)

  // Худший статус по ВСЕМ направлениям (включая скрытые) — задаёт цвет строки. Если сервер передал
  // явный rowStatus (худший по всем, в т.ч. ролевым) — используем его, иначе считаем по directions+extra.
  const worst =
    rowStatus ??
    worstVariant([...directions.map((d) => d.status), ...(extra ? [extra.status] : [])])
  const visible = directions.slice(0, visibleCount)
  const hiddenNative = directions.slice(visibleCount)
  const detailDirections = details ?? directions
  // Своя колонка «Прочее» есть, если у ЭТОЙ строки реально что-то скрыто; иначе колонка всё равно
  // резервируется, если её просит родитель (у других строк таблицы есть скрытые направления).
  const hasOwnOther = Boolean(extra && extra.count > 0) || hiddenNative.length > 0
  const showOtherCol = hasOwnOther || Boolean(showOtherColumn)
  const gridTemplate = trafficGridTemplate(visible.length, showOtherCol)

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={setOpen}
      className={cn('border-b border-line border-l-4 last:border-b-0', BORDER_BY_WORST[worst], className)}
    >
      {/* MR !17: строка — не одна большая кнопка, а область с двумя отдельными интерактивными
          элементами: ссылка с ФИО и кнопка-шеврон. Клик по остальной площади переключает
          раскрытие (мышиный аффорданс); с клавиатуры доступны ссылка и кнопка, поэтому сама
          область в таб-порядке не нужна и помечена role="presentation". */}
      <div
        data-testid="traffic-row"
        role="presentation"
        onClick={() => setOpen((v) => !v)}
        style={{ gridTemplateColumns: gridTemplate }}
        className={cn(
          'grid w-full cursor-pointer items-center gap-2.5 px-4 py-3 text-left transition-colors',
          'hover:bg-hover',
        )}
      >
        <div className="min-w-0">
          {employeeHref ? (
            <a
              href={employeeHref}
              onClick={(e) => {
                // Переход по ссылке не раскрывает и не сворачивает строку (MR !17).
                e.stopPropagation()
                onEmployeeClick?.(e)
              }}
              className="ds-focus-ring rounded-4 block truncate font-semibold text-txt hover:text-accent hover:underline"
            >
              {employee}
            </a>
          ) : (
            <div className="truncate font-semibold text-txt">{employee}</div>
          )}
          {sub ? <div className="truncate text-12 text-txt-secondary">{sub}</div> : null}
        </div>
        {/* Чипы и «+N» — визуальные: доступное имя кнопки = сотрудник + подпись, а полная
            семантика направлений (статус/срок/idx) раскрывается в списке деталей ниже. Каждый чип —
            отдельный grid-item в СВОЕЙ колонке (см. trafficGridTemplate), поэтому N-й чип всегда
            стоит под N-м заголовком TrafficRowHeader. */}
        {visible.map((d) => (
          <span
            key={`${d.name}:${d.idx ?? ''}`}
            aria-hidden
            title={`${d.name}${d.idx ? ` (${d.idx})` : ''}: ${STATUS_WORD[d.status]}${
              d.term ? `, ${d.term}` : ''
            }`}
            className={cn(CHIP_CLASS, CHIP_BY_STATUS[d.status])}
          >
            {d.term ?? d.idx ?? STATUS_WORD[d.status]}
          </span>
        ))}
        {showOtherCol ? (
          extra && extra.count > 0 ? (
            <span
              aria-hidden
              title={`Прочее: ${STATUS_WORD[extra.status]}`}
              className={cn(CHIP_CLASS, CHIP_BY_STATUS[extra.status])}
            >
              +{extra.count}
            </span>
          ) : hiddenNative.length > 0 ? (
            <span aria-hidden className={cn(CHIP_CLASS, 'bg-gray-bg text-gray')}>
              +{hiddenNative.length}
            </span>
          ) : (
            <span aria-hidden className={cn(CHIP_CLASS, 'text-txt-tertiary')}>
              —
            </span>
          )
        ) : null}
        {/* Кнопка раскрытия: своё состояние она сообщает вспомогательным технологиям через
            aria-expanded (проставляет Radix), клик не должен второй раз пройти по области строки. */}
        <Collapsible.Trigger asChild>
          <button
            type="button"
            aria-label={`Записи обучения: ${employee}`}
            onClick={(e) => e.stopPropagation()}
            className="ds-focus-ring rounded-4 flex size-4 shrink-0 items-center justify-center justify-self-end text-txt-tertiary"
          >
            <ChevronDown
              aria-hidden
              className={cn('size-4 stroke-current transition-transform', open && 'rotate-180')}
            />
          </button>
        </Collapsible.Trigger>
      </div>

      <Collapsible.Content>
        <div className="border-t border-line bg-subtle px-4 py-3">
          {detailDirections.length > 0 ? (
            <div className="ds-surface rounded-10">
              {detailDirections.map((d) => (
                <div
                  key={`${d.name}:${d.idx ?? ''}`}
                  className="flex items-center gap-2.5 border-t border-line px-3.5 py-2.5 first:border-t-0"
                >
                  <StatusDot variant={d.status} label={STATUS_WORD[d.status]} />
                  {d.idx ? (
                    <span className="rounded-pill bg-gray-bg px-1.5 py-0.5 text-11 text-gray">
                      {d.idx}
                    </span>
                  ) : null}
                  <span className="flex-1 text-13 font-medium text-txt">{d.name}</span>
                  {d.term ? <span className="text-12 text-txt-secondary">{d.term}</span> : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="px-1 text-12 text-txt-secondary">Нет данных по обучениям.</p>
          )}
          {footer ? <div className="mt-3">{footer}</div> : null}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

export interface TrafficRowHeaderColumn {
  key: string
  label: string
}

export interface TrafficRowHeaderProps {
  /** Заголовок первой колонки (по умолчанию «Сотрудник»). */
  employeeLabel?: string
  /**
   * Направления-колонки в порядке отображения (F2-16): берутся из данных (например,
   * `JournalResponse.columns`), а НЕ зашиваются в компонент — состав/порядок колонок задаёт сервер
   * по профилю клиента.
   */
  columns: TrafficRowHeaderColumn[]
  /** Заголовок агрегата скрытых направлений (по умолчанию «Прочее»). */
  otherLabel?: string
  /** Показывать колонку «Прочее» — истинно, если хотя бы у одной строки таблицы есть скрытые направления. */
  showOtherColumn?: boolean
  className?: string
}

/**
 * TrafficRowHeader — строка заголовков колонок над TrafficRow (F2-16, замечание менеджера F2-16):
 * «Сотрудник» → направления из `columns` (порядок = порядок чипов) → «Прочее» (если есть скрытые).
 * Ширины/порядок колонок ОБЩИЕ с TrafficRow через `trafficGridTemplate` — заголовок N всегда стоит
 * над чипом N. Чисто визуальный (как чипы строк): полная семантика — в раскрытии TrafficRow.
 */
export function TrafficRowHeader({
  employeeLabel = 'Сотрудник',
  columns,
  otherLabel = 'Прочее',
  showOtherColumn,
  className,
}: TrafficRowHeaderProps) {
  return (
    <div
      data-testid="traffic-row-header"
      style={{ gridTemplateColumns: trafficGridTemplate(columns.length, Boolean(showOtherColumn)) }}
      className={cn(
        'grid items-center gap-2.5 border-b border-l-4 border-line border-l-transparent bg-header px-4 py-2.5',
        className,
      )}
    >
      <span className="text-12 font-semibold text-txt-tertiary">{employeeLabel}</span>
      {/* Заголовок переносится по словам; слово, которое не влезает и в минимальную колонку
          (переименованное направление), делится по правилам переноса (lang="ru"), а не
          обрывается на произвольной букве. */}
      {columns.map((c) => (
        <span
          key={c.key}
          className="hyphens-auto text-center text-12 font-semibold leading-tight text-txt-tertiary"
        >
          {c.label}
        </span>
      ))}
      {showOtherColumn ? (
        <span className="text-center text-12 font-semibold leading-tight text-txt-tertiary">{otherLabel}</span>
      ) : null}
      <span aria-hidden />
    </div>
  )
}
